export type BatchStatus = 'Uploading' | 'Pending' | 'Processing' | 'Completed' | 'Cancelled';

/**
 * Statuses that mean the batch is still doing work and the UI should keep
 * polling and showing in-progress indicators (spinner, "running in
 * background" banner, etc.). `Completed` and `Cancelled` are terminal.
 */
const IN_PROGRESS_BATCH_STATUSES: ReadonlySet<BatchStatus> = new Set<BatchStatus>([
  'Uploading',
  'Pending',
  'Processing',
]);

export const isBatchInProgress = (batch: { status: string }): boolean =>
  IN_PROGRESS_BATCH_STATUSES.has(batch.status as BatchStatus);

export interface BatchStatusResult {
  batchId: string;
  status: BatchStatus;
  createdAtUtc: string;
  completedAtUtc: string | null;
  totalFiles: number;
  completedFiles: number;
  succeededFiles: number;
  failedFiles: number;
  items: BatchItemResult[];
}

export interface BatchItemResult {
  fileName: string;
  status:
    | 'Pending'
    | 'Analyzing'
    | 'Succeeded'
    | 'Failed'
    | 'Skipped'
    | 'Uploading'
    | 'Uploaded'
    | 'Cancelled';
  processedAtUtc: string | null;
  householdId: number | null;
  principalName: string | null;
  warnings: string[];
  errors: string[];
  summary: string | null;
  sourceSystem: string | null;
  spouseName: string | null;
  dependentCount: number;
  accountCount: number;
  goalCount: number;
  incomeCount: number;
  expenseCount: number;
  otherAssetCount: number;
  insuranceCount: number;
  trustCount: number;
  riskToleranceScore: number | null;
  principalAge: number | null;
  spouseAge: number | null;
  netWorth: number | null;
  annualIncome: number | null;
  planningInsights: PlanningInsight[];
}

export interface PlanningInsight {
  title: string;
  description: string;
  category: string;
}
